import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { HeartStraight, ArrowRight, X } from "@phosphor-icons/react";

/**
 * SundayWellnessBanner — nudges the client to update their wellness score
 * for the week. Renders only when the backend says `needs_prompt` is true
 * (last wellness check-in is 7+ days old OR never logged). Dismissable for
 * the current day via localStorage so a client who plans to log later doesn't
 * see the banner three times in a Sunday session.
 */
const DISMISS_KEY = "ll:sunday-wellness:dismissed";

export default function SundayWellnessBanner() {
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get("/client/wellness/status");
        setStatus(data);
      } catch { /* silent — banner just won't show */ }
    })();
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      if (raw === new Date().toISOString().slice(0, 10)) setDismissed(true);
    } catch { /* localStorage blocked */ }
  }, []);

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(DISMISS_KEY, new Date().toISOString().slice(0, 10)); } catch { /* noop */ }
  };

  if (!status?.needs_prompt || dismissed) return null;

  const isSunday = new Date().getDay() === 0;
  const headline = isSunday ? "Sunday check-in — update your wellness" : "Your wellness score is stale";
  const bodyCopy = status.days_since == null
    ? "You haven't logged wellness yet. 60 seconds — sleep, energy, stress, mood."
    : `Last logged ${status.days_since} day${status.days_since === 1 ? "" : "s"} ago. Quick update — sleep, energy, stress, mood.`;

  return (
    <div
      className="border-2 border-[#FF3333] bg-gradient-to-br from-[#FF3333]/15 via-[#FF3333]/5 to-transparent p-4 sm:p-5 relative mb-4"
      data-testid="sunday-wellness-banner"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 bg-[#FF3333] flex items-center justify-center shrink-0">
          <HeartStraight size={20} weight="fill" className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="id-label text-[#FF3333] mb-1">— Weekly wellness —</div>
          <div className="font-oswald text-xl sm:text-2xl uppercase tracking-tight leading-tight text-white mb-1">
            {headline}
          </div>
          <p className="text-zinc-300 font-manrope text-xs sm:text-sm">{bodyCopy}</p>
          <Link
            to="/app/stats?wellness=1"
            className="mt-3 inline-flex items-center gap-2 text-xs font-oswald uppercase tracking-[0.15em] font-bold text-white bg-[#FF3333] hover:bg-[#E62E2E] px-3 py-2 transition-colors"
            data-testid="sunday-wellness-cta"
          >
            Update wellness now <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
        <button
          onClick={dismiss}
          className="text-zinc-500 hover:text-white shrink-0"
          aria-label="Dismiss"
          data-testid="sunday-wellness-dismiss"
        >
          <X size={16} weight="bold" />
        </button>
      </div>
    </div>
  );
}
